import {gql} from '@apollo/client';

import {Purchase} from 'web-app/react/purchases/queries';

export const ChildForPurchase = gql`
    query ChildForPurchase($childIds: [ChildId!]) {
        children(childIds: $childIds) {
            id
            group {
                id
            }
            institutionId
        }
    }
`;

const PurchaseModalPricing = gql`
    fragment PurchaseModalPricing on Pricing {
        adHocPrice
        period
        pricingGroupId
        volume
    }
`;

const PurchaseModalProductAvailability = gql`
    fragment PurchaseModalProductAvailability on ProductAvailability {
        day
        productAvailabilityId
    }
`;

const PurchaseModalSessionAvailability = gql`
    fragment PurchaseModalSessionAvailability on SessionAvailability {
        day
        sessionAvailabilityId
        timeRange {
            from
            to
        }
    }
`;

export const GetPricingGroupIdForParentBookings = gql`
    query GetPricingGroupIdForParentBookings($institutionId: InstitutionId!) {
        finance {
            parentPurchaseSettings {
                getParentPurchaseSettings(institutionId: $institutionId) {
                    pricingGroupId
                    ruleGroupId
                }
            }
        }
    }
`;

export const GetSessionsAndProducts = gql`
    query GetSessionsAndProducts($childIds: [ChildId!], $validOnDate: LocalDate) {
        finance {
            revenueItems {
                productVersions(childIds: $childIds, validOnDate: $validOnDate) {
                    color
                    pricing {
                        ...PurchaseModalPricing
                    }
                    productAvailabilities {
                        ...PurchaseModalProductAvailability
                    }
                    productId
                    productVersionId
                    title
                    validFrom
                    validTo
                }
                sessionVersions(childIds: $childIds, validOnDate: $validOnDate) {
                    flexibleHours
                    type
                    color
                    pricing {
                        ...PurchaseModalPricing
                    }
                    sessionAvailabilities {
                        ...PurchaseModalSessionAvailability
                    }
                    sessionId
                    sessionVersionId
                    title
                    validFrom
                    validTo
                }
            }
        }
    }

    ${PurchaseModalPricing}
    ${PurchaseModalProductAvailability}
    ${PurchaseModalSessionAvailability}
`;

export const UpsertPurchase = gql`
    mutation UpsertPurchase($purchases: [PurchaseInput!]!, $ordering: PurchaseOrdering) {
        finance {
            purchases {
                update(purchases: $purchases) {
                    ...Purchase
                    ordering(ordering: $ordering)
                }
            }
        }
    }
    ${Purchase}
`;

export const DeletePurchase = gql`
    mutation DeletePurchase($purchaseIds: [PurchaseId!]!) {
        finance {
            purchases {
                delete(purchaseIds: $purchaseIds)
            }
        }
    }
`;

export const RejectPurchase = gql`
    mutation RejectPurchase($purchaseIds: [PurchaseId!]!, $message: String!) {
        finance {
            purchases {
                reject(purchaseIds: $purchaseIds, message: $message)
            }
        }
    }
`;

export const GetPurchaseForModal = gql`
    query GetPurchaseForModal($purchaseIds: [PurchaseId!]) {
        finance {
            purchases {
                filter(purchaseIds: $purchaseIds, count: 1) {
                    purchases {
                        discountAmount
                        id
                        invoiceStatus
                        rejectedAt
                        rejectionMessage
                        behaviors {
                            behaviorId
                        }
                        productBookings {
                            amount
                            pricingGroupId
                            bookedPrice
                            productAvailability {
                                productAvailabilityId
                                product {
                                    color
                                    pricing {
                                        ...PurchaseModalPricing
                                    }
                                    productAvailabilities {
                                        ...PurchaseModalProductAvailability
                                    }
                                    productId
                                    productVersionId
                                    title
                                    validFrom
                                    validTo
                                }
                            }
                            productPurchaseId
                            purchaseDate
                        }
                        sessionBookings {
                            pricingGroupId
                            purchaseDate
                            bookedPrice
                            sessionAvailability {
                                sessionAvailabilityId
                                timeRange {
                                    from
                                    to
                                }
                                session {
                                    flexibleHours
                                    type
                                    color
                                    pricing {
                                        ...PurchaseModalPricing
                                    }
                                    sessionAvailabilities {
                                        ...PurchaseModalSessionAvailability
                                    }
                                    sessionId
                                    sessionVersionId
                                    title
                                    validFrom
                                    validTo
                                }
                            }
                            sessionPurchaseId
                            sessionTime {
                                from
                                to
                            }
                        }
                        type
                        note
                        noteVisibleToParents
                    }
                }
                rejected: filter(purchaseIds: $purchaseIds, count: 1, rejected: true) {
                    purchases {
                        discountAmount
                        id
                        rejectedAt
                        rejectionMessage
                        behaviors {
                            behaviorId
                        }
                        productBookings {
                            amount
                            pricingGroupId
                            bookedPrice
                            productAvailability {
                                productAvailabilityId
                                product {
                                    color
                                    pricing {
                                        ...PurchaseModalPricing
                                    }
                                    productAvailabilities {
                                        ...PurchaseModalProductAvailability
                                    }
                                    productId
                                    productVersionId
                                    title
                                    validFrom
                                    validTo
                                }
                            }
                            productPurchaseId
                            purchaseDate
                        }
                        sessionBookings {
                            pricingGroupId
                            purchaseDate
                            bookedPrice
                            sessionAvailability {
                                sessionAvailabilityId
                                timeRange {
                                    from
                                    to
                                }
                                session {
                                    flexibleHours
                                    type
                                    color
                                    pricing {
                                        ...PurchaseModalPricing
                                    }
                                    sessionAvailabilities {
                                        ...PurchaseModalSessionAvailability
                                    }
                                    sessionId
                                    sessionVersionId
                                    title
                                    validFrom
                                    validTo
                                }
                            }
                            sessionPurchaseId
                            sessionTime {
                                from
                                to
                            }
                        }
                        type
                        note
                        noteVisibleToParents
                    }
                }
            }
        }
    }

    ${PurchaseModalPricing}
    ${PurchaseModalProductAvailability}
    ${PurchaseModalSessionAvailability}
`;

export const GetBehaviorsForPurchaseModal = gql`
    query GetBehaviorsForPurchaseModal($childId: ChildId, $institutionId: InstitutionSetId!) {
        finance {
            revenueItems {
                behaviors(institutionId: $institutionId) {
                    behaviorId
                }
            }
            purchases {
                behaviors(childId: $childId) {
                    behaviorId
                    ... on BehaviorCanCreatePurchase {
                        autoApprove
                    }
                    ... on BehaviorCanSeePurchaseAvailability {
                        rooms
                    }
                }
            }
        }
    }
`;

export const FreeSeats = gql`
    query FreeSeats(
        $ranges: [ClosedLocalDateRange!]!
        $institutionSetId: InstitutionSetId!
        $buckets: [ClosedLocalTimeRange!]
        $rooms: [GroupId!]
        $ignorePurchaseIds: [PurchaseId!]
    ) {
        attendance {
            summary(institutionSetId: $institutionSetId, ranges: $ranges, ignorePurchaseIds: $ignorePurchaseIds) {
                range {
                    from
                    to
                }
                rooms(rooms: $rooms) {
                    room {
                        id
                    }
                    slots(buckets: $buckets) {
                        timeRange {
                            from
                            to
                        }
                        vacancy
                    }
                }
            }
        }
    }
`;
